import "./TextField.scss";
import React, { useCallback, useEffect, useState } from 'react';
import * as _ from "lodash";
import { useSelector } from "react-redux";
import { RootState, useThunkDispatch } from "../../../store";
import { updateParameterValue } from "../../../store/storyline/actions";
import { TextField as BaseTextField } from "../../../shared/components";
import { DocumentedComponent } from '../../../shared/components/DocumentedComponent';
import { getDataProps } from "../../../shared/utilities";

type TextFieldProps = {
    name: string;
    initialValue?: string;
    separator?: string;
    trimWhitespace?: boolean;
    characterLimit?: number;
};

const TextField: React.FC<TextFieldProps> = (props) => {
    const { name, separator, trimWhitespace, characterLimit, initialValue, ...rest } = props;
    const parameterValue = useSelector((state: RootState) => state.storyline?.parameterValues?.get?.(name));
    const dispatch = useThunkDispatch();


    const getValueFromText = useCallback((text: string) => separator ?
        _.chain(_.split(text ?? "", separator)).map(s => trimWhitespace ? _.trim(s) : s).filter(a => !!a).value() :
        text ?? "",
        [separator, trimWhitespace]
    );

    const getTextFromValue = useCallback((value: any) => separator ?
        _.join(value ?? [], trimWhitespace ? separator + " " : separator) :
        value ?? "",
        [separator, trimWhitespace]
    );

    const [value, setValue] = useState<string>(getTextFromValue(parameterValue ?? initialValue));

    useEffect(() => {
        if (parameterValue === undefined) return;

        const textFromParameterValue = getTextFromValue(parameterValue);
        if (textFromParameterValue !== value) {
            setValue(textFromParameterValue);
        }
    }, [parameterValue, getTextFromValue]);

    const validationError = characterLimit && value?.length > characterLimit ? `Character limit of ${characterLimit} exceeded.` : undefined;
    const characterCountText = characterLimit && `${value?.length ?? 0}/${characterLimit}`;
    const helperText = [validationError, characterCountText].filter(s => !!s).join(" ");

    const handleInput = useCallback((event: React.FormEvent<HTMLDivElement>) => {
        const newText = (event.target as HTMLInputElement).value;
        setValue(newText);

        // Handle browser autocomplete events by immediately updating the parameter value...
        if (!(event.nativeEvent instanceof UIEvent)) {
            dispatch(updateParameterValue(name, getValueFromText(newText)));
        }
    }, [name, getValueFromText]);

    const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newText = event.target.value;
        dispatch(updateParameterValue(name, getValueFromText(newText)));
    }, [name, getValueFromText]);

    return (
        <BaseTextField
            name={name}
            fullWidth
            className="textfield"
            variant="outlined"
            error={!!validationError}
            helperText={helperText}
            value={value}
            onInput={handleInput}
            onBlur={handleBlur}
            {...rest}
            {...getDataProps(props)}
        />
    );
};

(TextField as DocumentedComponent).metadata = {
    description: "The TextField component allows the user to enter arbitrary text for storage in a variable.  This control supports both single-value and multi-value input (via the use of a customizable delimiter character).",
    isSelfClosing: true,
    attributes: [
        { name: `name`, type: `string`, description: "The name of the parameter that the value(s) will be read from and persisted to. The underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value." },
        { name: `initialValue`, type: `string`, description: "The initial value of the text field.  Only applies when the parameter with the specified name is empty (or if no parameter is specified). Optional - no default." },
        { name: `separator`, type: `string`, description: "The character or sequence of characters that will be used to separate multiple values.  In most cases this would be a comma (`,`) or semicolon (`;`).  If no separator is specified, the text field is a single-value input and the text entered is bound directly to the variable." },
        { name: `trimWhitespace`, type: `boolean`, description: "If `true`, whitespace characters are removed from the beginning and end of the values obtained by separating the string along the specified `separator` characters.  E.g., `\"apple, banana\"` would return `[\"apple\", \"banana\"]` if `true` and `[\"apple\", \" banana\"]` if this value is `false`." },
        { name: `characterLimit`, type: `number`, description: "The maximum number of characters that can be entered into the text field.  If the limit is exceeded, an error message will be displayed below the text field. This also includes a character counter underneath the `TextField`.  Optional - no default." },
    ]
};

export default TextField;